<script setup lang="ts">
import { Link } from '@inertiajs/vue3';
import { Check, Star, ArrowRight, X } from 'lucide-vue-next';
import { Button } from '@/components/ui/button';
import { plans } from '@/data/plans';
import { register, pricing } from '@/routes';

const planButtons = plans.map((p) => ({
    ...p,
    buttonText: p.code === 'free' ? 'Commencer gratuitement' : 'Passer à Pro',
}));
</script>

<template>
    <section id="pricing" class="border-t border-border/40 py-24">
        <div class="mx-auto max-w-5xl px-4">
            <div class="text-center">
                <span
                    class="mb-4 inline-block text-xs font-medium tracking-widest text-brand uppercase"
                >
                    Tarifs
                </span>
                <h2
                    class="text-3xl font-semibold tracking-tight text-foreground sm:text-4xl"
                >
                    Tarification simple et transparente
                </h2>
                <p class="mx-auto mt-4 max-w-xl text-muted-foreground">
                    L'outil complet est gratuit. L'IA est en option. Pas de
                    surprise, pas de carte bancaire requise.
                </p>
            </div>

            <div
                class="mx-auto mt-14 grid max-w-2xl grid-cols-1 gap-6 sm:grid-cols-2"
            >
                <div
                    v-for="plan in planButtons"
                    :key="plan.code"
                    class="relative flex flex-col rounded-2xl border p-8 transition-colors"
                    :class="
                        plan.popular
                            ? 'border-brand/40 bg-brand/[0.02]'
                            : 'border-border/40 hover:border-border'
                    "
                >
                    <div v-if="plan.popular" class="absolute -top-3.5 left-6">
                        <span
                            class="inline-flex items-center gap-1.5 rounded-full bg-brand px-3.5 py-1 text-xs font-medium text-white"
                        >
                            <Star class="h-3 w-3 fill-current" />
                            Populaire
                        </span>
                    </div>

                    <div class="mb-6">
                        <h3 class="text-lg font-semibold text-foreground">
                            {{ plan.name }}
                        </h3>
                        <div class="mt-3 flex items-baseline gap-1">
                            <span
                                class="text-4xl font-semibold tracking-tight text-foreground"
                                >€{{ plan.price }}</span
                            >
                            <span class="text-sm text-muted-foreground"
                                >/mois</span
                            >
                        </div>
                        <p class="mt-2 text-sm text-muted-foreground">
                            {{ plan.description }}
                        </p>
                    </div>

                    <ul class="mb-8 flex-1 space-y-3">
                        <li
                            v-for="feature in plan.features"
                            :key="feature.text"
                            class="flex items-start gap-2.5"
                        >
                            <Check
                                v-if="feature.included"
                                class="mt-0.5 h-4 w-4 shrink-0 text-brand"
                            />
                            <X
                                v-else
                                class="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground/30"
                            />
                            <span
                                class="text-sm"
                                :class="
                                    feature.included
                                        ? 'text-muted-foreground'
                                        : 'text-muted-foreground/40 line-through'
                                "
                            >
                                {{ feature.text }}
                            </span>
                        </li>
                    </ul>

                    <Button
                        as-child
                        :variant="plan.popular ? 'default' : 'outline'"
                        size="lg"
                        class="w-full rounded-full font-medium"
                        :class="
                            plan.popular ? 'bg-brand hover:bg-brand/85' : ''
                        "
                    >
                        <Link :href="register()">{{ plan.buttonText }}</Link>
                    </Button>
                </div>
            </div>

            <div class="mt-10 space-y-3 text-center">
                <Link
                    :href="pricing()"
                    class="inline-flex items-center gap-2 rounded-full border border-brand/30 px-6 py-2.5 text-sm font-medium text-brand transition-colors hover:bg-brand/5"
                >
                    Voir le comparatif complet
                    <ArrowRight class="h-4 w-4" />
                </Link>
                <p class="text-sm text-muted-foreground">
                    Pas de carte bancaire requise. Exportez vos données à tout
                    moment.
                </p>
            </div>
        </div>
    </section>
</template>
